<template>
  <PForm nested :schema="r$" :validate-on="['blur', 'input']" class="model-edit-form-project">
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <PFormField sr-only label="Projectnaam" name="name">
        <PInput v-model="project.name" placeholder="Projectnaam" ref="inputName">
          <template #leading>
            <FontAwesomeIcon size="sm" icon="file-contract" title="Projectnaam" />
          </template>
        </PInput>
      </PFormField>

      <PFormField label="Financieel verantwoordelijke" sr-only name="executor">
        <KnownBusinessEntityContactCombobox
          v-model="project.executor"
          ref="inputExecutor"
          input-placeholder="Financieel verantwoordelijke"
          grow
          :allowed-roles="[RoleTypes.FINANCIAL_OFFICER]"
          :selected-item-display-fn="getDisplayValue"
          @item-created="onExecutorCreated"
        >
          <template #before-input>
            <FontAwesomeIcon size="sm" icon="coins" title="Financieel verantwoordelijke" />
          </template>
        </KnownBusinessEntityContactCombobox>
      </PFormField>

      <PFormField label="Contactpersoon" sr-only name="contact">
        <KnownBusinessEntityContactCombobox
          v-model="project.contact"
          ref="inputPerson"
          input-placeholder="Contactpersoon"
          grow
          :selected-item-display-fn="getDisplayValue"
          @keydown.tab="onContactTabOut"
          @is-creating-new-item="isCreatingNewContact = $event"
        >
          <template #before-input>
            <FontAwesomeIcon size="sm" icon="address-card" title="Contactpersoon" />
          </template>
        </KnownBusinessEntityContactCombobox>
      </PFormField>

      <PFormField label="Interne projectopmerking">
        <div class="relative h-full grow">
          <PTextareaMention
            autoresize
            v-model="project.descriptionPlanner"
            placeholder="Beschrijf interne project afspraken of bijzonderheden"
            ref="plannerDescriptionTextArea"
            @file-trigger="plannerDescriptionFileUploader?.open()"
          >
            <template #trailing>
              <FileUploader
                ref="plannerDescriptionFileUploader"
                :context="FileUploadContexts.PLANNING"
                :file-bucket="project.fileBucket"
                show-file-count
                icons-class="surface"
                :override-is-uploading="isUploading"
                @file-uploaded="
                  (fileRelation) => {
                    onFileUploaded(fileRelation)
                    plannerDescriptionTextArea?.insertUploadedFile(fileRelation)
                  }
                "
                @is-uploading="isUploading = $event"
              />
            </template>
          </PTextareaMention>
          <FileDropzone
            :context="FileUploadContexts.PLANNING"
            :file-bucket="project.fileBucket"
            @file-uploaded="onFileUploaded"
            @is-uploading="isUploading = $event"
          />
        </div>
      </PFormField>

      <PFormField label="Projectopmerking voor dienstverleners">
        <div class="relative h-full grow">
          <PTextareaMention
            autoresize
            v-model="project.descriptionServiceProvider"
            placeholder="Beschrijf wat dienstverleners van het project moeten weten"
            ref="serviceProviderDescriptionTextArea"
            @file-trigger="serviceProviderDescriptionFileUploader?.open()"
          >
            <template #trailing>
              <FileUploader
                ref="serviceProviderDescriptionFileUploader"
                :context="FileUploadContexts.PLANNING"
                :file-bucket="project.fileBucketServiceProviderAttachments"
                show-file-count
                icons-class="surface"
                :override-is-uploading="isServiceProviderDescriptionUploading"
                @file-uploaded="
                  (fileRelation) => {
                    onServiceProviderFileUploaded(fileRelation)
                    serviceProviderDescriptionTextArea?.insertUploadedFile(fileRelation)
                  }
                "
                @is-uploading="isServiceProviderDescriptionUploading = $event"
              />
            </template>
          </PTextareaMention>
        </div>
      </PFormField>
    </div>
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import { computed, watchEffect, inject, onMounted, ref, useTemplateRef } from "vue"
import { useRegle } from "@regle/core"
import PFormField from "@/components/form/PFormField.vue"
import PInput from "@/components/form/PInput.vue"
import type { Project } from "@/models/project.model"
import { requiredValidator } from "@/helper/validation/validators"
import PTextareaMention from "@/components/form/PTextareaMention.vue"
import { upperFirst } from "lodash-es"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { ProjectController } from "@/controllers/model-controllers/project-controller"
import { isUniqueNameAndExecutorValidator } from "@/helper/validation/project-validation"
import KnownBusinessEntityContactCombobox from "@/components/known_business_entity_contact/KnownBusinessEntityContactCombobox.vue"
import { formFocusHack } from "@/helper/form-helper"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { FileUploadContexts } from "@/enums/file-upload-contexts"
import FileDropzone from "@/components/file/FileDropzone.vue"
import type { FileRelation } from "@/models/file/file-relation.model"
import FileUploader from "@/components/file/FileUploader.vue"
import { RoleTypes } from "@/enums/role-types.ts"
import { Person } from "@/models/person.model"

const inputName = ref()
const plannerDescriptionTextArea = useTemplateRef("plannerDescriptionTextArea")
const plannerDescriptionFileUploader = useTemplateRef("plannerDescriptionFileUploader")
const serviceProviderDescriptionTextArea = useTemplateRef("serviceProviderDescriptionTextArea")
const serviceProviderDescriptionFileUploader = useTemplateRef(
  "serviceProviderDescriptionFileUploader"
)

const isUploading = ref(false)
const isServiceProviderDescriptionUploading = ref(false)

function onFileUploaded(fileRelation: FileRelation) {
  project.value.fileBucket.fileRelations.push(fileRelation)
}

function onServiceProviderFileUploaded(fileRelation: FileRelation) {
  project.value.fileBucketServiceProviderAttachments.fileRelations.push(fileRelation)
}

const controller: ProjectController = inject("projectController")!
const { model: project, initialText, isFormInvalid } = useFormConfigurator<Project>({ controller })

onMounted(() => {
  if (initialText) {
    project.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, project, "name")
})

function getDisplayValue(kbec: KnownBusinessEntityContact) {
  return `${kbec.person?.fullName} | ${kbec.knownBusinessEntityName}`
}

const isCreatingNewContact = ref(false)
function onContactTabOut() {
  if (isCreatingNewContact.value) {
    // In case new item option was selected using tab key, prevent auto select behavior. Else the selected value
    // will contaminate the ModelEditFormPerson.
    return
  }
  // If contact is left empty, match it to the executor
  if (!project.value.contact && project.value.executor) {
    project.value.contact = project.value.executor
  }
}

function onExecutorCreated(personOrKBEC: KnownBusinessEntityContact | Person) {
  if (personOrKBEC instanceof KnownBusinessEntityContact) {
    project.value.executor = personOrKBEC
  }
}
const rules = {
  name: {
    requiredValidator,
    isUniqueNameAndExecutorValidator: isUniqueNameAndExecutorValidator(project)
  },
  executor: { requiredValidator },
  contact: { requiredValidator }
}

const { r$ } = useRegle(
  computed(() => ({
    name: project.value.name,
    executor: project.value.executor ? true : undefined,
    contact: project.value.contact ? true : undefined
  })),
  rules,
  { autoDirty: false }
)

watchEffect(() => {
  isFormInvalid.value = r$.$invalid
})
</script>

<style scoped></style>
